import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function getSupplierDisplayName(supplier) {
  return String(
    supplier?.clientname ||
      supplier?.clientName ||
      supplier?.name ||
      supplier?.supplierName ||
      "",
  ).trim();
}

function normalizeSupplierContacts(supplier) {
  const companyName = getSupplierDisplayName(supplier);
  const fallbackEmail = String(supplier?.email || "").trim();
  const fallbackPhone = String(
    supplier?.phonenumber || supplier?.phoneNumber || "",
  ).trim();
  const contacts = Array.isArray(supplier?.contacts) ? supplier.contacts : [];

  const rows = [];
  const seen = new Set();

  const pushContact = (contact, index = 0) => {
    const email = String(
      contact?.email || contact?.supplierMailId || contact?.mailId || "",
    ).trim();
    if (!email) return;
    const key = email.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({
      key: String(contact?._id || contact?.id || key),
      contactPerson: String(
        contact?.contactPerson || contact?.name || "",
      ).trim(),
      supplierMailId: email,
      supplierName: companyName,
      supplierPhoneNumber: String(
        contact?.phonenumber ||
          contact?.phoneNumber ||
          contact?.supplierPhoneNumber ||
          "",
      ).trim(),
      index,
    });
  };

  contacts.forEach((contact, index) => pushContact(contact, index));

  if (rows.length === 0 && fallbackEmail) {
    rows.push({
      key: fallbackEmail.toLowerCase(),
      contactPerson: String(supplier?.contactPerson || "").trim(),
      supplierMailId: fallbackEmail,
      supplierName: companyName,
      supplierPhoneNumber: fallbackPhone,
      index: 0,
    });
  }

  return rows;
}

function buildSupplierContactsPayload(selectedKeys, contactOptions) {
  const keySet =
    selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
  return (contactOptions || [])
    .filter((row) => keySet.has(row.key))
    .map((row) => ({
      supplierMailId: row.supplierMailId,
      supplierName: row.supplierName,
      supplierPhoneNumber: row.supplierPhoneNumber || "",
    }));
}

const TABS = [
  { id: "details", label: "GI Details" },
  { id: "material", label: "Material" },
  { id: "quantities", label: "Quantities & Stock" },
];

const WHOSE_STOCK_OPTIONS = ["Client", "InstaTenders"];

const emptyHeader = () => ({
  giNumber: "",
  giDate: new Date().toISOString().slice(0, 10),
  poNumber: "",
  poDate: "",
  po_id: "",
  supplierName: "",
  supplierId: "",
  invoiceNumber: "",
  invoiceDate: "",
  whoseStock: "",
});

const getHeaderSnapshot = (source) => ({
  giNumber: String(source?.giNumber || "").trim(),
  giDate: String(source?.giDate || "").trim(),
  poNumber: String(source?.poNumber || "").trim(),
  poDate: String(source?.poDate || "").trim(),
  po_id: String(source?.po_id || "").trim(),
  supplierName: String(source?.supplierName || "").trim(),
  supplierId: String(source?.supplierId || "").trim(),
  invoiceNumber: String(source?.invoiceNumber || "").trim(),
  invoiceDate: String(source?.invoiceDate || "").trim(),
  whoseStock: String(source?.whoseStock || "").trim(),
});

const isSameHeaderSnapshot = (a, b) => {
  if (!a || !b) return false;
  return Object.keys(a).every(
    (key) => String(a[key] || "").trim() === String(b[key] || "").trim(),
  );
};

const SUPPLIER_SEARCH_MIN_LENGTH = 2;
const SUPPLIER_SEARCH_DEBOUNCE_MS = 400;

const createMaterialRow = (overrides = {}) => ({
  rowKey: `gi-mat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  materialCode: "",
  materialDescription: "",
  hsnCode: "",
  uom: "",
  po_id: "",
  poQuantity: "",
  poBalanceQuantity: "",
  balanceCredit: "",
  acceptedSoFar: "",
  rejectedSoFar: "",
  inwardSoFar: "",
  inwardQuantity: "",
  acceptedQuantity: "",
  rejectedQuantity: "0",
  stock: "",
  availableStock: "",
  ...overrides,
});

const toDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const toNumberOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
};

const qtyString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
};

const parseQtyNum = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/** Credit this GI's reserved qty back onto API balance for edit validation/display. */
const getGiReservationCredit = (reservations, giId, fallbackAccepted = 0) => {
  const id = String(giId || "").trim();
  if (id && Array.isArray(reservations) && reservations.length) {
    const mine = reservations.find(
      (r) => String(r?.giId || r?._id || "").trim() === id,
    );
    if (mine) {
      return parseQtyNum(mine.acceptedQuantity ?? mine.inwardQuantity);
    }
  }
  return parseQtyNum(fallbackAccepted);
};

const withEditablePoBalance = (apiBalance, credit) =>
  qtyString(parseQtyNum(apiBalance) + parseQtyNum(credit));

/** Available = Stock + Accepted (this GI) */
const computeAvailableStock = (row) => {
  const stock = parseFloat(row?.stock);
  const accepted = parseFloat(row?.acceptedQuantity);
  const a = Number.isFinite(stock) ? stock : 0;
  const b = Number.isFinite(accepted) ? accepted : 0;
  return String(a + b);
};

const extractPoList = (payload) => {
  if (Array.isArray(payload?.data?.pos)) return payload.data.pos;
  if (Array.isArray(payload?.data?.poList)) return payload.data.poList;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.pos)) return payload.pos;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

/** Normalize API id fields (string, ObjectId, or populated doc). */
const extractEntityId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    const s = String(value).trim();
    return !s || s === "[object Object]" ? "" : s;
  }
  if (typeof value === "object") {
    return extractEntityId(value._id ?? value.id ?? value.$oid ?? "");
  }
  return "";
};

/** Convert API dates (DD-MM-YYYY or ISO) to YYYY-MM-DD for <input type="date"> */
const toDateInputValue = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return "";
};

const cleanDash = (value) => {
  const s = value == null ? "" : String(value).trim();
  return !s || s === "-" ? "" : s;
};

const extractPoDetail = (payload) => {
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return payload.data;
  }
  if (payload && typeof payload === "object" && (payload.poNumber || payload._id)) {
    return payload;
  }
  return null;
};

const extractCreatedGiId = (payload) => {
  const candidates = [
    payload?.data?._id,
    payload?.data?.id,
    payload?.data?.gi?._id,
    payload?.gi?._id,
    payload?._id,
  ];
  return candidates.find((id) => id != null && String(id).trim() !== "") || null;
};

const extractGiNumber = (payload) => {
  const candidates = [
    payload?.data?.giNumber,
    payload?.giNumber,
    payload?.data?.gi?.giNumber,
  ];
  return candidates.find((n) => n != null && String(n).trim() !== "") || null;
};

const extractBalanceMaterials = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  if (Array.isArray(data?.materials)) return data.materials;
  if (Array.isArray(payload?.materials)) return payload.materials;
  return [];
};

const extractMaterialCatalog = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data?.materials)) return data.materials;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.materialList)) return data.materialList;
  if (Array.isArray(data)) return data;
  if (Array.isArray(payload?.materials)) return payload.materials;
  return [];
};

const mapCatalogMaterialToGiFields = (m) => ({
  materialCode: cleanDash(m?.materialCode || m?.code),
  materialDescription:
    m?.productName || m?.materialDescription || m?.description || "",
  hsnCode: cleanDash(m?.hsnCode || m?.hsn),
  uom: cleanDash(m?.uom || m?.unit),
  materialMasterId: String(m?._id || m?.id || "").trim(),
});

function pickGiFromResponse(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (Array.isArray(dataBlock.goodsInwards) && dataBlock.goodsInwards[0]) {
    return dataBlock.goodsInwards[0];
  }
  if (dataBlock.goodsInwards && typeof dataBlock.goodsInwards === "object") {
    return dataBlock.goodsInwards;
  }
  if (Array.isArray(dataBlock) && dataBlock[0]) {
    return dataBlock[0];
  }
  if (dataBlock.gi && typeof dataBlock.gi === "object") {
    return dataBlock.gi;
  }
  if (
    !Array.isArray(dataBlock) &&
    (dataBlock._id != null ||
      dataBlock.id != null ||
      dataBlock.giNumber != null)
  ) {
    return dataBlock;
  }
  return null;
}

const mapPoMaterial = (m, index = 0, poHeaderId = "") => {
  const id = m?._id || m?.id || "";
  const description =
    m?.materialDescription || m?.productName || m?.description || "";
  const quantity =
    m?.quantity ?? m?.poQuantity ?? m?.qty ?? m?.orderedQuantity ?? "";
  const code = cleanDash(m?.materialCode || m?.code);
  const balance =
    m?.poBalanceQuantity ?? m?.remaining ?? quantity;

  return {
    sourceKey: id || `${code || "mat"}|${description}|${index}`,
    po_id: id ? String(id) : poHeaderId ? String(poHeaderId) : "",
    materialCode: code,
    materialDescription: description,
    hsnCode: cleanDash(m?.hsnCode || m?.hsn),
    uom: cleanDash(m?.uom),
    poQuantity: qtyString(quantity),
    poBalanceQuantity: qtyString(balance),
    acceptedSoFar: qtyString(m?.acceptedSoFar ?? 0),
    rejectedSoFar: qtyString(m?.rejectedSoFar ?? 0),
    inwardSoFar: qtyString(m?.inwardSoFar ?? 0),
  };
};

const formatReservationDate = (value) => {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
};

const mapBalanceMaterial = (m, index = 0, poHeaderId = "") => {
  const code = cleanDash(m?.materialCode || m?.code);
  const description = m?.materialDescription || m?.productName || "";
  const poQty = m?.poQuantity ?? m?.quantity ?? "";
  const balance = m?.poBalanceQuantity ?? m?.remaining ?? poQty;
  const linePoId = m?.po_id || m?.poId || poHeaderId || "";
  const reservations = Array.isArray(m?.reservations)
    ? m.reservations.map((r, i) => ({
        key: String(r?.giId || r?._id || r?.giNumber || i),
        giId: String(r?.giId || r?._id || "").trim(),
        giNumber: String(r?.giNumber || "").trim(),
        giDate: r?.giDate || "",
        status: String(r?.status || "").trim().toLowerCase(),
        acceptedQuantity: qtyString(r?.acceptedQuantity ?? 0),
        inwardQuantity: qtyString(r?.inwardQuantity ?? 0),
        rejectedQuantity: qtyString(r?.rejectedQuantity ?? 0),
        createdByName:
          (r?.createdBy && typeof r.createdBy === "object"
            ? r.createdBy.name || r.createdBy.userName || r.createdBy.email
            : r?.createdBy) || "",
      }))
    : [];

  return {
    sourceKey: `${code || "mat"}|${description}|${index}`,
    po_id: linePoId ? String(linePoId) : "",
    materialCode: code,
    materialDescription: description,
    hsnCode: cleanDash(m?.hsnCode || m?.hsn),
    uom: cleanDash(m?.uom),
    poQuantity: qtyString(poQty),
    poBalanceQuantity: qtyString(balance),
    acceptedSoFar: qtyString(m?.acceptedSoFar ?? 0),
    acceptedActive: qtyString(m?.acceptedActive ?? 0),
    acceptedDraft: qtyString(m?.acceptedDraft ?? 0),
    rejectedSoFar: qtyString(m?.rejectedSoFar ?? 0),
    inwardSoFar: qtyString(m?.inwardSoFar ?? 0),
    hasDraftReservation:
      m?.hasDraftReservation === true ||
      reservations.some((r) => r.status === "draft"),
    reservations,
    stock: qtyString(m?.stock ?? m?.stockQuantity ?? ""),
  };
};

const buildGiRowFromPoLine = (m) => {
  const fallbackQty = m.poQuantity || "";

  return createMaterialRow({
    sourceKey: m.sourceKey,
    materialCode: m.materialCode || "",
    materialDescription: m.materialDescription || "",
    hsnCode: m.hsnCode || "",
    uom: m.uom || "",
    po_id: m.po_id || "",
    poQuantity: m.poQuantity || "",
    poBalanceQuantity: qtyString(
      m.poBalanceQuantity !== "" && m.poBalanceQuantity != null
        ? m.poBalanceQuantity
        : fallbackQty,
    ),
    acceptedSoFar: m.acceptedSoFar || "",
    rejectedSoFar: m.rejectedSoFar || "",
    inwardSoFar: m.inwardSoFar || "",
    // Inward / Accepted are entered manually on the Material tab
    inwardQuantity: "",
    acceptedQuantity: "",
    rejectedQuantity: "0",
    availableStock: computeAvailableStock({
      stock: m.stock || "",
      acceptedQuantity: "",
    }),
  });
};

function GoodsInwardsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const poSearchRef = useRef(null);
  const supplierSearchRef = useRef(null);
  const supplierSearchRequestIdRef = useRef(0);
  const supplierSearchDebounceRef = useRef(null);
  const [activeTab, setActiveTab] = useState("details");
  const [form, setForm] = useState(() => emptyHeader());
  const [materials, setMaterials] = useState([]);
  const [giId, setGiId] = useState(null);
  const [giStatus, setGiStatus] = useState("draft");
  const [isLoadingGi, setIsLoadingGi] = useState(true);
  const [savedHeaderSnapshot, setSavedHeaderSnapshot] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [poSearchTerm, setPoSearchTerm] = useState("");
  const [poResults, setPoResults] = useState([]);
  const [isSearchingPo, setIsSearchingPo] = useState(false);
  const [showPoDropdown, setShowPoDropdown] = useState(false);
  const [isLoadingPoDetail, setIsLoadingPoDetail] = useState(false);
  const [poMaterials, setPoMaterials] = useState([]);
  /** PO id whose material lines are currently loaded; empty = needs load on Next. */
  const [poIdForLoadedMaterials, setPoIdForLoadedMaterials] = useState("");
  const [selectedPoKeys, setSelectedPoKeys] = useState([]);
  const [poMaterialSearchApplied, setPoMaterialSearchApplied] = useState("");
  const [poMaterialResultsOpen, setPoMaterialResultsOpen] = useState(true);
  const [stockLoadingKeys, setStockLoadingKeys] = useState({});

  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [supplierResults, setSupplierResults] = useState([]);
  const [isSearchingSupplier, setIsSearchingSupplier] = useState(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [supplierContactOptions, setSupplierContactOptions] = useState([]);
  const [selectedSupplierContactKeys, setSelectedSupplierContactKeys] = useState(
    () => new Set(),
  );

  const [materialCatalog, setMaterialCatalog] = useState([]);
  const [isLoadingMaterialCatalog, setIsLoadingMaterialCatalog] =
    useState(false);
  const [materialCodeDropdownRowKey, setMaterialCodeDropdownRowKey] =
    useState(null);
  const [materialCodeDropdownRect, setMaterialCodeDropdownRect] = useState(null);
  const materialCatalogLoadedRef = useRef(false);
  const materialCodeInputRefs = useRef({});

  const isStandaloneGi = !String(form.po_id || "").trim();
  const hasSupplierSelected = Boolean(
    String(form.supplierId || "").trim() ||
      String(form.supplierName || "").trim(),
  );
  const canAddStandaloneMaterials = isStandaloneGi && hasSupplierSelected;

  const updateForm = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const updateMaterialRow = (rowKey, name, value) => {
    const qtyErrors = {};

    setMaterials((prev) =>
      prev.map((row) => {
        if (row.rowKey !== rowKey) return row;

        const next = { ...row, [name]: value };

        if (name === "inwardQuantity" || name === "acceptedQuantity") {
          const inward = parseFloat(next.inwardQuantity);
          const accepted = parseFloat(next.acceptedQuantity);
          const poQty = parseFloat(next.poQuantity);
          const balance = parseFloat(next.poBalanceQuantity);
          const maxInward = Number.isFinite(balance)
            ? balance
            : Number.isFinite(poQty)
              ? poQty
              : null;

          if (Number.isFinite(inward) && Number.isFinite(accepted)) {
            next.rejectedQuantity = String(Math.max(0, inward - accepted));
          }

          if (name === "acceptedQuantity") {
            next.availableStock = computeAvailableStock(next);
          }

          if (Number.isFinite(inward) && maxInward != null && inward > maxInward) {
            qtyErrors[`mat_${rowKey}_inwardQuantity`] =
              "Inward quantity not more than PO balance";
          }

          if (Number.isFinite(accepted)) {
            if (Number.isFinite(poQty) && accepted > poQty) {
              qtyErrors[`mat_${rowKey}_acceptedQuantity`] =
                "Accepted quantity not more than PO quantity";
            } else if (Number.isFinite(balance) && accepted > balance) {
              qtyErrors[`mat_${rowKey}_acceptedQuantity`] =
                "Accepted quantity not more than PO balance";
            } else if (Number.isFinite(inward) && accepted > inward) {
              qtyErrors[`mat_${rowKey}_acceptedQuantity`] =
                "Accepted quantity not more than Inward";
            }
          }
        }

        return next;
      }),
    );

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.materials;
      delete next[`mat_${rowKey}_${name}`];
      delete next[`mat_${rowKey}_rejectedQuantity`];

      if (name === "inwardQuantity" || name === "acceptedQuantity") {
        delete next[`mat_${rowKey}_acceptedQuantity`];
        delete next[`mat_${rowKey}_inwardQuantity`];
      }

      return { ...next, ...qtyErrors };
    });
  };

  const removeMaterialRow = (rowKey) => {
    setMaterials((prev) => prev.filter((row) => row.rowKey !== rowKey));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (poSearchRef.current && !poSearchRef.current.contains(event.target)) {
        setShowPoDropdown(false);
      }
      if (
        supplierSearchRef.current &&
        !supplierSearchRef.current.contains(event.target)
      ) {
        setShowSupplierDropdown(false);
      }
      const inMaterialCodeSearch = event.target.closest?.(
        "[data-gi-material-code-search='true']",
      );
      const inMaterialCodeDropdown = event.target.closest?.(
        "[data-gi-material-code-dropdown='true']",
      );
      if (!inMaterialCodeSearch && !inMaterialCodeDropdown) {
        setMaterialCodeDropdownRowKey(null);
        setMaterialCodeDropdownRect(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current);
      }
    };
  }, []);

  const fetchMaterialCatalog = useCallback(async ({ force = false } = {}) => {
    if (!force && materialCatalogLoadedRef.current) return;
    setIsLoadingMaterialCatalog(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-material",
        page: 1,
        limit: 5000,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setMaterialCatalog(extractMaterialCatalog(responseData));
        materialCatalogLoadedRef.current = true;
      } else {
        setMaterialCatalog([]);
        toast.error(
          responseData?.message || "Failed to load materials",
          { autoClose: 2500 },
        );
      }
    } catch (error) {
      console.error("Material catalog error:", error);
      setMaterialCatalog([]);
      toast.error("Failed to load materials", { autoClose: 2500 });
    } finally {
      setIsLoadingMaterialCatalog(false);
    }
  }, []);

  useEffect(() => {
    if (canAddStandaloneMaterials) {
      fetchMaterialCatalog();
    }
  }, [canAddStandaloneMaterials, fetchMaterialCatalog]);

  const updateMaterialCodeDropdownPosition = useCallback((rowKey) => {
    const el = materialCodeInputRefs.current[rowKey];
    if (!el) {
      setMaterialCodeDropdownRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setMaterialCodeDropdownRect({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
  }, []);

  const openMaterialCodeDropdown = useCallback(
    (rowKey) => {
      setMaterialCodeDropdownRowKey(rowKey);
      requestAnimationFrame(() => updateMaterialCodeDropdownPosition(rowKey));
      fetchMaterialCatalog();
    },
    [updateMaterialCodeDropdownPosition, fetchMaterialCatalog],
  );

  useEffect(() => {
    if (!materialCodeDropdownRowKey) return undefined;
    const onReposition = () =>
      updateMaterialCodeDropdownPosition(materialCodeDropdownRowKey);
    onReposition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [materialCodeDropdownRowKey, updateMaterialCodeDropdownPosition]);

  const getFilteredMaterialCatalog = useCallback(
    (searchTerm) => {
      const q = String(searchTerm || "").trim().toLowerCase();
      if (!q) return [];
      return materialCatalog
        .filter((m) => {
          const code = String(m.materialCode || m.code || "").toLowerCase();
          const name = String(
            m.productName || m.materialDescription || m.description || "",
          ).toLowerCase();
          const hsn = String(m.hsnCode || m.hsn || "").toLowerCase();
          return code.includes(q) || name.includes(q) || hsn.includes(q);
        })
        .slice(0, 40);
    },
    [materialCatalog],
  );

  const applyCatalogMaterialToRow = (rowKey, material) => {
    const mapped = mapCatalogMaterialToGiFields(material);
    if (!mapped.materialCode) {
      toast.error("Selected material has no code", { autoClose: 2500 });
      return;
    }
    setMaterials((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? {
              ...row,
              ...mapped,
            }
          : row,
      ),
    );
    setMaterialCodeDropdownRowKey(null);
    setMaterialCodeDropdownRect(null);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.materials;
      delete next[`mat_${rowKey}_materialCode`];
      delete next[`mat_${rowKey}_materialDescription`];
      return next;
    });
  };

  const loadPoBalanceLines = useCallback(async (poId, options = {}) => {
    const { clearGiMaterials = false, currentGiId = "" } = options;
    if (!poId) {
      setPoMaterials([]);
      return;
    }
    setIsLoadingPoDetail(true);
    try {
      const [detailRes, balanceRes] = await Promise.all([
        axios.post(API_URL, {
          action: "get-po-detail-by-id",
          id: String(poId),
        }),
        axios.post(API_URL, {
          action: "get-po-material-balance",
          poId: String(poId),
        }),
      ]);

      const detailData = normalizeApiPayload(parseApiResponse(detailRes.data));
      const balanceData = normalizeApiPayload(parseApiResponse(balanceRes.data));
      const headerId = String(poId);

      let mappedMats = [];
      if (isApiSuccess(balanceData)) {
        const balanceMats = extractBalanceMaterials(balanceData);
        mappedMats = balanceMats.map((m, i) =>
          mapBalanceMaterial(m, i, headerId),
        );
      }

      if (!mappedMats.length) {
        const detail = isApiSuccess(detailData)
          ? extractPoDetail(detailData)
          : null;
        const rawMats =
          (Array.isArray(detail?.materials) && detail.materials.length
            ? detail.materials
            : []) || [];
        mappedMats = rawMats.map((m, i) => mapPoMaterial(m, i, headerId));
        if (!mappedMats.length && isApiSuccess(balanceData) === false) {
          console.warn(
            "get-po-material-balance failed:",
            balanceData?.message || "unknown",
          );
        }
      }

      setPoMaterials(mappedMats);
      setPoIdForLoadedMaterials(String(poId));
      setSelectedPoKeys([]);
      setPoMaterialSearchApplied("");
      setPoMaterialResultsOpen(true);
      if (clearGiMaterials) {
        setMaterials([]);
      } else if (mappedMats.length && currentGiId) {
        // Balance API deducts this GI's draft reservation — credit it back for edit
        setMaterials((prev) =>
          prev.map((row) => {
            const match = mappedMats.find(
              (m) =>
                String(m.materialCode || "") ===
                  String(row.materialCode || "") &&
                String(m.materialDescription || "") ===
                  String(row.materialDescription || ""),
            );
            if (!match) return row;
            const credit = getGiReservationCredit(
              match.reservations,
              currentGiId,
              row.acceptedQuantity,
            );
            return {
              ...row,
              poBalanceQuantity: withEditablePoBalance(
                match.poBalanceQuantity,
                credit,
              ),
              balanceCredit: qtyString(credit),
              acceptedSoFar: match.acceptedSoFar || row.acceptedSoFar,
            };
          }),
        );
      }
    } catch (error) {
      console.error("Load PO balance error:", error);
      setPoMaterials([]);
      setPoIdForLoadedMaterials("");
    } finally {
      setIsLoadingPoDetail(false);
    }
  }, []);

  /** Look up PO id by po number when GI record has number but no po_id. */
  const resolvePoIdByNumber = useCallback(async (poNumber) => {
    const q = String(poNumber || "").trim();
    if (!q) return "";
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-po",
        page: 1,
        limit: 20,
        search: q,
        poStatus: "Approved",
        status: "active",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) return "";
      const list = extractPoList(responseData);
      const match =
        list.find(
          (p) =>
            String(p.poNumber || p.poNo || "")
              .trim()
              .toLowerCase() === q.toLowerCase(),
        ) || list[0];
      return extractEntityId(match?._id || match?.id);
    } catch (error) {
      console.error("Resolve PO id by number error:", error);
      return "";
    }
  }, []);

  /**
   * Ensure Material tab has PO lines for the current PO.
   * Works when PO was re-chosen OR when GI Details were left unchanged
   * (resolves missing po_id from poNumber when needed).
   * Skips for standalone GI (supplier-only, no PO).
   */
  const ensurePoMaterialsForMaterialTab = useCallback(async () => {
    const poNumber = String(form.poNumber || poSearchTerm || "").trim();
    let poId = extractEntityId(form.po_id);

    if (!poId && materials.length) {
      poId = extractEntityId(materials[0]?.po_id);
    }

    if (!poId && poNumber) {
      poId = await resolvePoIdByNumber(poNumber);
      if (poId) {
        setForm((prev) => ({ ...prev, po_id: poId, poNumber: poNumber || prev.poNumber }));
      }
    }

    // Standalone GI — no PO required
    if (!poId && !poNumber) {
      return true;
    }

    if (!poId) {
      toast.error(
        "Could not resolve PO id for this PO number. Re-select the PO from the dropdown.",
        { autoClose: 3000 },
      );
      return false;
    }

    // Already loaded for this PO — keep list; no extra API
    if (
      poId === String(poIdForLoadedMaterials || "").trim() &&
      poMaterials.length > 0
    ) {
      return true;
    }

    await loadPoBalanceLines(poId, {
      clearGiMaterials: false,
      currentGiId: giId || id || "",
    });
    return true;
  }, [
    form.po_id,
    form.poNumber,
    poSearchTerm,
    materials,
    poIdForLoadedMaterials,
    poMaterials.length,
    resolvePoIdByNumber,
    loadPoBalanceLines,
    giId,
    id,
  ]);

  const applySupplierContacts = useCallback((supplier, preferredEmails = []) => {
    const contacts = normalizeSupplierContacts(supplier);
    setSupplierContactOptions(contacts);
    const preferred = new Set(
      (preferredEmails || [])
        .map((email) => String(email || "").trim().toLowerCase())
        .filter(Boolean),
    );
    if (preferred.size > 0) {
      const matched = contacts
        .filter((row) => preferred.has(row.supplierMailId.toLowerCase()))
        .map((row) => row.key);
      setSelectedSupplierContactKeys(
        new Set(matched.length > 0 ? matched : contacts.map((row) => row.key)),
      );
    } else {
      setSelectedSupplierContactKeys(new Set(contacts.map((row) => row.key)));
    }
  }, []);

  const clearSupplierContacts = useCallback(() => {
    setSupplierContactOptions([]);
    setSelectedSupplierContactKeys(new Set());
  }, []);

  const loadSupplierContactsByIdOrName = useCallback(
    async ({ supplierId, supplierName, preferredEmails = [] }) => {
      const sid = String(supplierId || "").trim();
      const name = String(supplierName || "").trim();
      if (!sid && !name) {
        clearSupplierContacts();
        return;
      }

      try {
        const userRole = localStorage.getItem("role");
        const payload = {
          action: "get-suppliers",
          status: "active",
          search: name || sid,
          limit: 25,
        };
        if (userRole !== "admin") {
          payload.status = "active";
        }
        const response = await axios.post(API_URL, payload);
        const responseData = normalizeApiPayload(parseApiResponse(response.data));
        const list =
          responseData?.suppliers || responseData?.data?.suppliers || [];
        if (
          !isApiSuccess(responseData) ||
          !Array.isArray(list) ||
          list.length === 0
        ) {
          clearSupplierContacts();
          return;
        }

        const matched =
          list.find(
            (row) =>
              String(row.id || row._id || "").trim() === sid ||
              getSupplierDisplayName(row).toLowerCase() === name.toLowerCase(),
          ) || list[0];

        applySupplierContacts(matched, preferredEmails);
      } catch (error) {
        console.error("Load supplier contacts error:", error);
        clearSupplierContacts();
      }
    },
    [applySupplierContacts, clearSupplierContacts],
  );

  useEffect(() => {
    let cancelled = false;

    const fetchGiForEdit = async () => {
      if (!id) {
        toast.error("Valid id is required", { autoClose: 2500 });
        navigate("/goodsInwards");
        return;
      }

      setIsLoadingGi(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-goods-inwards-by-id",
          id,
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );

        if (cancelled) return;

        if (!isApiSuccess(responseData)) {
          toast.error(
            responseData?.message || "Failed to fetch goods inwards",
            { autoClose: 3000 },
          );
          navigate("/goodsInwards");
          return;
        }

        const record = pickGiFromResponse(responseData);
        if (!record) {
          toast.error("Goods inwards not found", { autoClose: 3000 });
          navigate("/goodsInwards");
          return;
        }

        const apiMaterials = Array.isArray(record.materials)
          ? record.materials
          : [];
        const poNumber = String(record.poNumber || "").trim();
        let poId =
          extractEntityId(record.po_id) ||
          extractEntityId(record.poId) ||
          extractEntityId(record.purchaseOrder) ||
          extractEntityId(apiMaterials[0]?.po_id) ||
          extractEntityId(apiMaterials[0]?.poId);

        // GI may store poNumber without po_id — resolve so Next works without re-select
        if (!poId && poNumber) {
          poId = await resolvePoIdByNumber(poNumber);
        }

        const recordId = String(record._id || record.id || id);

        const nextForm = {
          giNumber: record.giNumber || "",
          giDate: toDateInputValue(record.giDate) || emptyHeader().giDate,
          poNumber,
          poDate: toDateInputValue(record.poDate),
          po_id: poId,
          supplierName: record.supplierName || "",
          supplierId: String(
            record.supplierId ||
              record.supplier?._id ||
              record.supplier?.id ||
              "",
          ).trim(),
          invoiceNumber: record.invoiceNumber || "",
          invoiceDate: toDateInputValue(record.invoiceDate),
          whoseStock: record.whoseStock || "",
        };
        setGiId(recordId);
        setGiStatus(String(record.status || "draft").toLowerCase());
        setForm(nextForm);
        setSavedHeaderSnapshot(getHeaderSnapshot(nextForm));
        setPoSearchTerm(poNumber);

        const savedContacts = Array.isArray(record.supplierContacts)
          ? record.supplierContacts
          : [];
        const preferredEmails = savedContacts.map(
          (c) => c.supplierMailId || c.email || "",
        );
        if (record.supplier && typeof record.supplier === "object") {
          applySupplierContacts(
            {
              ...record.supplier,
              clientname:
                record.supplierName || getSupplierDisplayName(record.supplier),
            },
            preferredEmails,
          );
        } else if (savedContacts.length > 0) {
          applySupplierContacts(
            {
              clientname: record.supplierName || "",
              contacts: savedContacts.map((c) => ({
                email: c.supplierMailId || c.email || "",
                phonenumber: c.supplierPhoneNumber || c.phonenumber || "",
                contactPerson: c.contactPerson || "",
              })),
            },
            preferredEmails,
          );
        } else if (nextForm.supplierId || nextForm.supplierName) {
          loadSupplierContactsByIdOrName({
            supplierId: nextForm.supplierId,
            supplierName: nextForm.supplierName,
            preferredEmails,
          });
        } else {
          clearSupplierContacts();
        }

        setMaterials(
          apiMaterials.map((m, index) => {
            const linePoId =
              extractEntityId(m.po_id) ||
              extractEntityId(m.poId) ||
              poId;
            const acceptedQty = qtyString(m.acceptedQuantity);
            const apiBalance = qtyString(m.poBalanceQuantity);
            // Draft reservation is already in balance=0 — credit this GI's qty back
            const balanceCredit = acceptedQty || qtyString(m.inwardQuantity);
            return createMaterialRow({
              sourceKey:
                m._id ||
                m.id ||
                `${cleanDash(m.materialCode)}|${m.materialDescription || ""}|${index}`,
              materialCode: cleanDash(m.materialCode),
              materialDescription: m.materialDescription || "",
              hsnCode: cleanDash(m.hsnCode || m.hsn),
              uom: cleanDash(m.uom),
              po_id: linePoId,
              poQuantity: qtyString(m.poQuantity),
              poBalanceQuantity: withEditablePoBalance(apiBalance, balanceCredit),
              balanceCredit,
              acceptedSoFar: qtyString(m.acceptedSoFar),
              rejectedSoFar: qtyString(m.rejectedSoFar),
              inwardSoFar: qtyString(m.inwardSoFar),
              inwardQuantity: qtyString(m.inwardQuantity),
              acceptedQuantity: acceptedQty,
              rejectedQuantity: qtyString(
                m.rejectedQuantity != null ? m.rejectedQuantity : 0,
              ),
              stock: qtyString(m.stock),
              availableStock: computeAvailableStock({
                stock: qtyString(m.stock),
                acceptedQuantity: acceptedQty,
              }),
            });
          }),
        );

        if (poId) {
          await loadPoBalanceLines(poId, {
            clearGiMaterials: false,
            currentGiId: recordId,
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Load goods inwards for edit error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(errData?.message || "Failed to load goods inwards", {
          autoClose: 3000,
        });
        navigate("/goodsInwards");
      } finally {
        if (!cancelled) setIsLoadingGi(false);
      }
    };

    fetchGiForEdit();
    return () => {
      cancelled = true;
    };
  }, [
    id,
    navigate,
    loadPoBalanceLines,
    resolvePoIdByNumber,
    applySupplierContacts,
    clearSupplierContacts,
    loadSupplierContactsByIdOrName,
  ]);

  const searchPurchaseOrders = useCallback(async (term) => {
    const q = String(term || "").trim();
    if (q.length < 1) {
      setPoResults([]);
      return;
    }

    setIsSearchingPo(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-po",
        page: 1,
        limit: 20,
        search: q,
        poStatus: "Approved",
        status: "active",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setPoResults(extractPoList(responseData));
      } else {
        setPoResults([]);
      }
    } catch (error) {
      console.error("PO search error:", error);
      setPoResults([]);
    } finally {
      setIsSearchingPo(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = String(poSearchTerm || "").trim();
      if (showPoDropdown && q.length >= 1) {
        searchPurchaseOrders(q);
      } else if (!q) {
        setPoResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [poSearchTerm, showPoDropdown, searchPurchaseOrders]);

  const searchSuppliers = useCallback(async (term, { showToast = false } = {}) => {
    const search = String(term || "").trim();
    if (search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierResults([]);
      return;
    }

    const requestId = ++supplierSearchRequestIdRef.current;
    setIsSearchingSupplier(true);

    try {
      const userRole = localStorage.getItem("role");
      const payload = {
        action: "get-suppliers",
        status: "active",
        search,
      };
      if (userRole !== "admin") {
        payload.status = "active";
      }

      const response = await axios.post(API_URL, payload);
      if (requestId !== supplierSearchRequestIdRef.current) return;

      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const list =
        responseData?.suppliers ||
        responseData?.data?.suppliers ||
        [];

      if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
        setSupplierResults(list);
        if (showToast) {
          toast.info(`${list.length} supplier(s) found.`, { autoClose: 2500 });
        }
      } else {
        setSupplierResults([]);
        if (showToast) {
          toast.info(responseData?.message || "No suppliers found", {
            autoClose: 2000,
          });
        }
      }
    } catch (error) {
      if (requestId !== supplierSearchRequestIdRef.current) return;
      console.error("Supplier search error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "Failed to search suppliers", {
        autoClose: 2000,
      });
      setSupplierResults([]);
    } finally {
      if (requestId === supplierSearchRequestIdRef.current) {
        setIsSearchingSupplier(false);
      }
    }
  }, []);

  const handleSupplierSearchChange = (value) => {
    setSupplierSearchTerm(value);
    setShowSupplierDropdown(
      String(value).trim().length >= SUPPLIER_SEARCH_MIN_LENGTH,
    );
    supplierSearchRequestIdRef.current += 1;

    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }

    if (form.supplierName || form.supplierId) {
      setForm((prev) => ({
        ...prev,
        supplierName: "",
        supplierId: "",
      }));
      setSupplierContactOptions([]);
      setSelectedSupplierContactKeys(new Set());
      setFieldErrors((prev) => {
        if (!prev.supplierName) return prev;
        const next = { ...prev };
        delete next.supplierName;
        return next;
      });
    }

    const trimmed = String(value || "").trim();
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setIsSearchingSupplier(false);
      setSupplierResults([]);
      return;
    }

    supplierSearchDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed, { showToast: false });
    }, SUPPLIER_SEARCH_DEBOUNCE_MS);
  };

  const toggleSupplierContact = (contactKey) => {
    setSelectedSupplierContactKeys((prev) => {
      const next = new Set(prev);
      if (next.has(contactKey)) next.delete(contactKey);
      else next.add(contactKey);
      return next;
    });
  };

  const applySupplierSelection = (supplier) => {
    const name = getSupplierDisplayName(supplier);
    const supplierId = String(supplier?.id || supplier?._id || "").trim();
    if (!name) {
      toast.error("Selected supplier has no name", { autoClose: 2500 });
      return;
    }

    setShowSupplierDropdown(false);
    setSupplierSearchTerm("");
    setSupplierResults([]);
    setForm((prev) => ({
      ...prev,
      supplierName: name,
      supplierId,
    }));
    applySupplierContacts(supplier);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.supplierName;
      return next;
    });

    if (!String(form.po_id || "").trim()) {
      setPoMaterials([]);
      setPoIdForLoadedMaterials("");
      setSelectedPoKeys([]);
      setPoMaterialSearchApplied("");
      setPoMaterialResultsOpen(true);
    }
  };

  const addStandaloneMaterialRow = () => {
    if (!canAddStandaloneMaterials) {
      toast.info("Select a supplier first to add materials", {
        autoClose: 2500,
      });
      return;
    }
    setMaterials((prev) => [...prev, createMaterialRow()]);
    setFieldErrors((prev) => {
      if (!prev.materials) return prev;
      const next = { ...prev };
      delete next.materials;
      return next;
    });
  };

  const clearMaterialSelection = () => {
    setPoMaterials([]);
    setPoIdForLoadedMaterials("");
    setSelectedPoKeys([]);
    setPoMaterialSearchApplied("");
    setPoMaterialResultsOpen(true);
    setMaterials([]);
    setStockLoadingKeys({});
  };

  /** Choose PO on Material tab — load header + balance (incl. draft reservations). */
  const applyPoSelection = async (po) => {
    const poId = po?._id || po?.id;
    if (!poId) {
      toast.error("Selected PO has no id", { autoClose: 2500 });
      return;
    }

    const previousPoId = String(form.po_id || "").trim();
    const poChanged = Boolean(previousPoId && previousPoId !== String(poId));

    setIsLoadingPoDetail(true);
    setShowPoDropdown(false);
    setPoSearchTerm(po.poNumber || po.poNo || "");

    try {
      const [detailRes, balanceRes] = await Promise.all([
        axios.post(API_URL, {
          action: "get-po-detail-by-id",
          id: poId,
        }),
        axios.post(API_URL, {
          action: "get-po-material-balance",
          poId: String(poId),
        }),
      ]);

      const detailData = normalizeApiPayload(parseApiResponse(detailRes.data));
      const balanceData = normalizeApiPayload(parseApiResponse(balanceRes.data));

      if (!isApiSuccess(detailData)) {
        toast.error(detailData?.message || "Failed to load PO details", {
          autoClose: 3000,
        });
        return;
      }

      const detail = extractPoDetail(detailData) || po;
      const supplier =
        detail?.supplierName ||
        detail?.supplier?.name ||
        detail?.vendorName ||
        po?.supplierName ||
        "";
      const poDate = toDateInputValue(
        detail?.poDate || detail?.date || po?.poDate || "",
      );
      const headerId = String(detail?._id || detail?.id || poId);
      const currentGiId = String(giId || id || "").trim();

      let mappedMats = [];
      if (isApiSuccess(balanceData)) {
        const balanceMats = extractBalanceMaterials(balanceData);
        mappedMats = balanceMats.map((m, i) =>
          mapBalanceMaterial(m, i, headerId),
        );
      } else {
        console.warn(
          "get-po-material-balance failed:",
          balanceData?.message || "unknown",
        );
        toast.warning(
          balanceData?.message ||
            "Could not load PO balance; using original PO quantities",
          { autoClose: 3500 },
        );
        const rawMats =
          (Array.isArray(detail?.materials) && detail.materials.length
            ? detail.materials
            : null) ||
          (Array.isArray(po?.materials) && po.materials.length
            ? po.materials
            : []) ||
          [];
        mappedMats = rawMats.map((m, i) => mapPoMaterial(m, i, headerId));
      }

      setForm((prev) => ({
        ...prev,
        poNumber:
          balanceData?.data?.poNumber ||
          detail?.poNumber ||
          detail?.poNo ||
          po?.poNumber ||
          "",
        poDate,
        po_id: headerId,
        supplierName: supplier,
        supplierId: String(
          detail?.supplierId ||
            detail?.supplier?.id ||
            detail?.supplier?._id ||
            po?.supplierId ||
            "",
        ).trim(),
      }));
      setSupplierSearchTerm("");
      setSupplierResults([]);
      setShowSupplierDropdown(false);
      const supplierIdForContacts = String(
        detail?.supplierId ||
          detail?.supplier?.id ||
          detail?.supplier?._id ||
          po?.supplierId ||
          "",
      ).trim();
      if (detail?.supplier && typeof detail.supplier === "object") {
        applySupplierContacts(detail.supplier);
      } else if (
        Array.isArray(detail?.supplierContacts) &&
        detail.supplierContacts.length > 0
      ) {
        applySupplierContacts({
          clientname: supplier,
          contacts: detail.supplierContacts,
        });
      } else {
        loadSupplierContactsByIdOrName({
          supplierId: supplierIdForContacts,
          supplierName: supplier,
        });
      }
      setPoMaterials(mappedMats);
      setPoIdForLoadedMaterials(headerId);
      setSelectedPoKeys([]);
      setPoMaterialSearchApplied("");
      setPoMaterialResultsOpen(true);
      if (poChanged) {
        setMaterials([]);
      } else if (mappedMats.length && currentGiId) {
        // Credit this draft GI's reserved qty back for edit validation
        setMaterials((prev) =>
          prev.map((row) => {
            const match = mappedMats.find(
              (m) =>
                String(m.materialCode || "") ===
                  String(row.materialCode || "") &&
                String(m.materialDescription || "") ===
                  String(row.materialDescription || ""),
            );
            if (!match) return row;
            const credit = getGiReservationCredit(
              match.reservations,
              currentGiId,
              row.acceptedQuantity,
            );
            return {
              ...row,
              poBalanceQuantity: withEditablePoBalance(
                match.poBalanceQuantity,
                credit,
              ),
              balanceCredit: qtyString(credit),
              acceptedSoFar: match.acceptedSoFar || row.acceptedSoFar,
            };
          }),
        );
      }
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.poNumber;
        delete next.materials;
        return next;
      });
    } catch (error) {
      console.error("PO detail/balance error:", error);
      toast.error("Failed to load PO details", { autoClose: 3000 });
    } finally {
      setIsLoadingPoDetail(false);
    }
  };

  const isPoMaterialAdded = useCallback(
    (m) => materials.some((row) => row.sourceKey === m.sourceKey),
    [materials],
  );

  const isPoMaterialClosed = (m) => {
    const bal = parseFloat(m.poBalanceQuantity);
    return Number.isFinite(bal) && bal <= 0;
  };

  const filteredPoMaterials = useMemo(() => {
    const q = String(poMaterialSearchApplied || "").trim().toLowerCase();
    if (!q) return poMaterials;
    return poMaterials.filter((m) => {
      const code = String(m.materialCode || "").toLowerCase();
      const desc = String(m.materialDescription || "").toLowerCase();
      const hsn = String(m.hsnCode || "").toLowerCase();
      return code.includes(q) || desc.includes(q) || hsn.includes(q);
    });
  }, [poMaterials, poMaterialSearchApplied]);

  const getSelectablePoKeys = useCallback(
    () =>
      filteredPoMaterials
        .filter((m) => !isPoMaterialAdded(m) && !isPoMaterialClosed(m))
        .map((m) => m.sourceKey),
    [filteredPoMaterials, isPoMaterialAdded],
  );

  const countPendingPoSelection = () =>
    getSelectablePoKeys().filter((key) => selectedPoKeys.includes(key)).length;

  const countAddedInCurrentResults = () =>
    filteredPoMaterials.filter((m) => isPoMaterialAdded(m)).length;

  const togglePoMaterialKey = (sourceKey) => {
    setSelectedPoKeys((prev) =>
      prev.includes(sourceKey)
        ? prev.filter((k) => k !== sourceKey)
        : [...prev, sourceKey],
    );
  };

  const toggleSelectAllPoMaterials = () => {
    const selectable = getSelectablePoKeys();
    const allSelected =
      selectable.length > 0 &&
      selectable.every((key) => selectedPoKeys.includes(key));
    if (allSelected) {
      setSelectedPoKeys((prev) => prev.filter((key) => !selectable.includes(key)));
    } else {
      setSelectedPoKeys((prev) => Array.from(new Set([...prev, ...selectable])));
    }
  };


  const addSelectedPoMaterials = () => {
    const selectable = getSelectablePoKeys();
    const toAdd = filteredPoMaterials.filter(
      (m) =>
        selectedPoKeys.includes(m.sourceKey) && selectable.includes(m.sourceKey),
    );
    if (!toAdd.length) {
      toast.info("Select one or more PO material lines to add", {
        autoClose: 2500,
      });
      return;
    }

    setMaterials((prev) => [...prev, ...toAdd.map(buildGiRowFromPoLine)]);
    setSelectedPoKeys((prev) =>
      prev.filter((key) => !toAdd.some((m) => m.sourceKey === key)),
    );
    setFieldErrors((prev) => {
      if (!prev.materials) return prev;
      const next = { ...prev };
      delete next.materials;
      return next;
    });
  };

  const pickStockValues = (inventory) => {
    if (!inventory || typeof inventory !== "object") {
      return { stock: "" };
    }
    const stockRaw =
      inventory.stockQuantity ??
      inventory.stock ??
      inventory.currentStock ??
      inventory.quantity ??
      "";

    return {
      stock:
        stockRaw === null || stockRaw === undefined || stockRaw === ""
          ? ""
          : String(stockRaw),
    };
  };

  const pickInventoryRecord = (responseData) => {
    let dataBlock =
      responseData?.data != null ? responseData.data : responseData;
    if (!dataBlock || typeof dataBlock !== "object") return null;
    if (dataBlock.inventory && typeof dataBlock.inventory === "object") {
      return dataBlock.inventory;
    }
    if (
      !Array.isArray(dataBlock) &&
      dataBlock.inventories == null &&
      (dataBlock._id != null ||
        dataBlock.materialCode != null ||
        dataBlock.stockQuantity != null)
    ) {
      return dataBlock;
    }
    if (Array.isArray(dataBlock.inventories) && dataBlock.inventories.length) {
      return dataBlock.inventories[0];
    }
    return null;
  };

  const fetchStockForRow = useCallback(async (row, { silent = false } = {}) => {
    const code = String(row.materialCode || "").trim();
    if (!code || code === "-") {
      if (!silent) {
        toast.info("Enter a material code to fetch stock", { autoClose: 2500 });
      }
      return;
    }

    setStockLoadingKeys((prev) => ({ ...prev, [row.rowKey]: true }));
    try {
      const response = await axios.post(API_URL, {
        action: "get-inventory-by-id",
        materialCode: code,
        id: code,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        const inventory = pickInventoryRecord(responseData);
        const { stock } = pickStockValues(inventory);

        setMaterials((prev) =>
          prev.map((m) =>
            m.rowKey === row.rowKey
              ? {
                  ...m,
                  stock: stock || "0",
                  availableStock: computeAvailableStock(m),
                }
              : m,
          ),
        );
      } else if (!silent) {
        toast.error(responseData?.message || "Inventory not found", {
          autoClose: 3000,
        });
        setMaterials((prev) =>
          prev.map((m) =>
            m.rowKey === row.rowKey
              ? {
                  ...m,
                  stock: "0",
                  availableStock: computeAvailableStock(m),
                }
              : m,
          ),
        );
      }
    } catch (error) {
      console.error("Inventory fetch error:", error);
      if (!silent) {
        toast.error("Failed to fetch inventory stock", { autoClose: 3000 });
      }
    } finally {
      setStockLoadingKeys((prev) => {
        const next = { ...prev };
        delete next[row.rowKey];
        return next;
      });
    }
  }, []);

  const fetchStockForAllRows = useCallback(
    async (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      await Promise.all(
        list.map((row) => fetchStockForRow(row, { silent: true })),
      );
    },
    [fetchStockForRow],
  );
  const validateDetails = () => {
    const errors = {};
    if (!String(form.invoiceNumber || "").trim()) {
      errors.invoiceNumber = "Invoice number is required";
    }
    if (!String(form.whoseStock || "").trim()) {
      errors.whoseStock = "Whose stock is required";
    }
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    if (Object.keys(errors).length) {
      toast.error("Please complete GI Details", { autoClose: 2500 });
      return false;
    }
    return true;
  };

  const validateMaterials = () => {
    const errors = {};
    const hasPo =
      String(form.poNumber || "").trim() && String(form.po_id || "").trim();
    if (!String(form.supplierName || "").trim()) {
      errors.supplierName = "Supplier name is required";
    }
    if (!hasPo && !String(form.supplierName || "").trim()) {
      errors.poNumber = "Select a PO or a supplier";
    }
    if (!materials.length) {
      errors.materials = "Add at least one material line";
    }

    materials.forEach((row) => {
      if (!String(row.materialCode || "").trim()) {
        errors[`mat_${row.rowKey}_materialCode`] = "Required";
      }
      if (!String(row.materialDescription || "").trim()) {
        errors[`mat_${row.rowKey}_materialDescription`] = "Required";
      }
    });

    setFieldErrors((prev) => ({ ...prev, ...errors }));
    if (Object.keys(errors).length) {
      if (errors.supplierName || errors.poNumber) {
        toast.error(
          hasPo
            ? "Complete supplier details"
            : "Select a PO or search and select a supplier",
          { autoClose: 2500 },
        );
      } else if (errors.materials) {
        toast.error("Add at least one material line", { autoClose: 2500 });
      } else {
        toast.error("Please complete required material fields", {
          autoClose: 2500,
        });
      }
      return false;
    }
    return true;
  };

  const validateQuantities = () => {
    const errors = {};
    let hasError = false;
    let toastMessage = "Please fix quantity errors in the materials table";

    materials.forEach((row) => {
      const inward = parseFloat(row.inwardQuantity);
      const accepted = parseFloat(row.acceptedQuantity);
      const rejected = parseFloat(row.rejectedQuantity);
      const balance = parseFloat(row.poBalanceQuantity);
      const poQty = parseFloat(row.poQuantity);
      const maxInward = Number.isFinite(balance)
        ? balance
        : Number.isFinite(poQty)
          ? poQty
          : null;

      if (!Number.isFinite(inward) || inward < 0) {
        errors[`mat_${row.rowKey}_inwardQuantity`] = "Invalid inward quantity";
        hasError = true;
      }
      if (!Number.isFinite(accepted) || accepted < 0) {
        errors[`mat_${row.rowKey}_acceptedQuantity`] =
          "Invalid accepted quantity";
        hasError = true;
      }
      if (!Number.isFinite(rejected) || rejected < 0) {
        errors[`mat_${row.rowKey}_rejectedQuantity`] = "Invalid rejected quantity";
        hasError = true;
      }
      if (Number.isFinite(accepted) && Number.isFinite(poQty) && accepted > poQty) {
        errors[`mat_${row.rowKey}_acceptedQuantity`] =
          "Accepted quantity not more than PO quantity";
        toastMessage = "Accepted quantity not more than PO quantity";
        hasError = true;
      } else if (
        Number.isFinite(accepted) &&
        Number.isFinite(balance) &&
        accepted > balance
      ) {
        errors[`mat_${row.rowKey}_acceptedQuantity`] =
          "Accepted quantity not more than PO balance";
        toastMessage = "Accepted quantity not more than PO balance";
        hasError = true;
      } else if (
        Number.isFinite(accepted) &&
        Number.isFinite(inward) &&
        accepted > inward
      ) {
        errors[`mat_${row.rowKey}_acceptedQuantity`] =
          "Accepted quantity not more than Inward";
        toastMessage = "Accepted quantity not more than Inward";
        hasError = true;
      }
      if (
        Number.isFinite(inward) &&
        maxInward != null &&
        inward > maxInward
      ) {
        errors[`mat_${row.rowKey}_inwardQuantity`] =
          "Inward quantity not more than PO balance";
        toastMessage = "Inward quantity not more than PO balance";
        hasError = true;
      }
    });

    setFieldErrors((prev) => ({ ...prev, ...errors }));
    if (hasError) {
      toast.error(toastMessage, { autoClose: 3000 });
      return false;
    }
    return true;
  };

  const hasInvalidMaterialQuantities = useMemo(() => {
    if (!materials.length) return false;
    return materials.some((row) => {
      const inward = parseFloat(row.inwardQuantity);
      const accepted = parseFloat(row.acceptedQuantity);
      const balance = parseFloat(row.poBalanceQuantity);
      const poQty = parseFloat(row.poQuantity);
      const maxInward = Number.isFinite(balance)
        ? balance
        : Number.isFinite(poQty)
          ? poQty
          : null;

      if (!Number.isFinite(inward) || inward < 0) return true;
      if (!Number.isFinite(accepted) || accepted < 0) return true;
      if (Number.isFinite(accepted) && Number.isFinite(poQty) && accepted > poQty) {
        return true;
      }
      if (
        Number.isFinite(accepted) &&
        Number.isFinite(balance) &&
        accepted > balance
      ) {
        return true;
      }
      if (Number.isFinite(accepted) && Number.isFinite(inward) && accepted > inward) {
        return true;
      }
      if (Number.isFinite(inward) && maxInward != null && inward > maxInward) {
        return true;
      }
      return false;
    });
  }, [materials]);

  const buildHeaderFields = () => {
    const fields = {
      invoiceNumber: String(form.invoiceNumber || "").trim(),
      whoseStock: String(form.whoseStock || "").trim(),
    };
    if (form.giDate) fields.giDate = form.giDate;
    if (form.invoiceDate) fields.invoiceDate = form.invoiceDate;
    if (String(form.poNumber || "").trim()) {
      fields.poNumber = String(form.poNumber).trim();
    }
    if (String(form.supplierName || "").trim()) {
      fields.supplierName = String(form.supplierName).trim();
    }
    if (String(form.supplierId || "").trim()) {
      fields.supplierId = String(form.supplierId).trim();
    }
    if (form.poDate) fields.poDate = form.poDate;
    if (form.po_id) fields.po_id = String(form.po_id).trim();

    const supplierContacts = buildSupplierContactsPayload(
      selectedSupplierContactKeys,
      supplierContactOptions,
    );
    if (supplierContacts.length > 0) {
      fields.supplierContacts = supplierContacts;
    }
    return fields;
  };

  const isHeaderDirty = () =>
    !isSameHeaderSnapshot(getHeaderSnapshot(form), savedHeaderSnapshot);

  const buildMaterialsPayload = () =>
    materials.map((row) => {
      const item = {
        materialCode: String(row.materialCode || "").trim(),
        materialDescription: String(row.materialDescription || "").trim(),
        uom: String(row.uom || "").trim(),
        poQuantity: toNumberOrEmpty(row.poQuantity),
        inwardQuantity: toNumberOrEmpty(row.inwardQuantity),
        acceptedQuantity: toNumberOrEmpty(row.acceptedQuantity),
        rejectedQuantity: toNumberOrEmpty(row.rejectedQuantity),
      };

      const hsn = String(row.hsnCode || "").trim();
      if (hsn) item.hsnCode = hsn;
      const linePoId = String(row.po_id || form.po_id || "").trim();
      if (linePoId) item.po_id = linePoId;

      const stock = toNumberOrEmpty(row.stock);
      const availableStock = toNumberOrEmpty(computeAvailableStock(row));
      if (stock !== "") item.stock = stock;
      if (availableStock !== "") item.availableStock = availableStock;

      return item;
    });

  const applyGiMetaFromResponse = (responseData) => {
    const id = extractCreatedGiId(responseData);
    if (id) setGiId(String(id));
    const giNumber = extractGiNumber(responseData);
    if (giNumber) {
      setForm((prev) => ({ ...prev, giNumber: String(giNumber) }));
    }
    const record = pickGiFromResponse(responseData);
    const nextStatus = record?.status ?? responseData?.data?.status;
    if (nextStatus) {
      setGiStatus(String(nextStatus).toLowerCase());
    }
    return id ? String(id) : null;
  };

  const saveDraftFromDetails = async () => {
    if (!validateDetails()) {
      setActiveTab("details");
      return null;
    }

    if (!giId) {
      toast.error("Goods inwards id is missing", { autoClose: 2500 });
      return null;
    }

    // No header changes — just continue to next tab without API call
    if (!isHeaderDirty()) {
      return giId;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const statusToSave = giStatus === "active" ? "active" : "draft";
      // Details tab: update header fields only (do not send materials)
      const payload = {
        action: "update-goods-inwards",
        id: giId,
        status: statusToSave,
        ...buildHeaderFields(),
      };
      if (form.giNumber) payload.giNumber = String(form.giNumber).trim();
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to update goods inwards",
          { autoClose: 3000 },
        );
        return null;
      }
      setSavedHeaderSnapshot(getHeaderSnapshot(form));
      toast.success(
        responseData?.message || "GI details updated successfully",
        { autoClose: 2000 },
      );
      return applyGiMetaFromResponse(responseData) || giId;
    } catch (error) {
      console.error("Update GI details error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating GI",
        { autoClose: 3000 },
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDraftWithMaterials = async () => {
    if (!validateDetails()) {
      setActiveTab("details");
      return false;
    }
    if (!validateMaterials()) {
      setActiveTab("material");
      return false;
    }
    if (!validateQuantities()) {
      setActiveTab("material");
      return false;
    }

    let currentGiId = giId;
    if (!currentGiId) {
      currentGiId = await saveDraftFromDetails();
      if (!currentGiId) return false;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const statusToSave = giStatus === "active" ? "active" : "draft";
      const payload = {
        action: "update-goods-inwards",
        id: currentGiId,
        status: statusToSave,
        ...buildHeaderFields(),
        materials: buildMaterialsPayload(),
      };
      if (form.giNumber) payload.giNumber = String(form.giNumber).trim();
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (!isApiSuccess(responseData)) {
        const balanceData = responseData?.data;
        if (
          balanceData &&
          (balanceData.remaining != null || balanceData.acceptedSoFar != null)
        ) {
          toast.error(
            responseData?.message ||
              `Accepted quantity exceeds remaining PO balance (${balanceData.remaining ?? "-"})`,
            { autoClose: 4000 },
          );
        } else {
          toast.error(
            responseData?.message || "Failed to update goods inwards",
            { autoClose: 3000 },
          );
        }
        return false;
      }

      applyGiMetaFromResponse(responseData);
      return true;
    } catch (error) {
      console.error("Update GI draft materials error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating GI materials",
        { autoClose: 3000 },
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivate = async () => {
    if (!validateDetails()) {
      setActiveTab("details");
      return;
    }
    if (!validateMaterials()) {
      setActiveTab("material");
      return;
    }
    if (!validateQuantities()) {
      setActiveTab("quantities");
      return;
    }

    if (!giId) {
      toast.error("Draft GI is missing. Please complete GI Details first.", {
        autoClose: 3000,
      });
      setActiveTab("details");
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const payload = {
        action: "update-goods-inwards",
        id: giId,
        status: "active",
        ...buildHeaderFields(),
        materials: buildMaterialsPayload(),
      };
      if (form.giNumber) payload.giNumber = String(form.giNumber).trim();
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Goods inwards activated successfully",
          { autoClose: 2500 },
        );

        const inventoryUpdates =
          responseData?.inventoryUpdates ||
          responseData?.data?.inventoryUpdates ||
          (responseData?.inventoryUpdate
            ? [responseData.inventoryUpdate]
            : []);

        if (Array.isArray(inventoryUpdates)) {
          inventoryUpdates.forEach((upd) => {
            if (upd && upd.applied === false && upd.reason) {
              toast.warning(
                `${upd.materialCode || "Material"}: ${upd.reason}`,
                { autoClose: 4000 },
              );
            }
          });
        }

        const id = extractCreatedGiId(responseData) || giId;
        navigate(id ? `/goodsInwardsView/${id}` : "/goodsInwards");
      } else {
        const balanceData = responseData?.data;
        if (
          balanceData &&
          (balanceData.remaining != null || balanceData.acceptedSoFar != null)
        ) {
          toast.error(
            responseData?.message ||
              `Accepted quantity exceeds remaining PO balance (${balanceData.remaining ?? "-"})`,
            { autoClose: 4000 },
          );
        } else {
          toast.error(
            responseData?.message || "Failed to activate goods inwards",
            { autoClose: 3000 },
          );
        }
      }
    } catch (error) {
      console.error("Activate goods inwards error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while activating goods inwards",
        { autoClose: 3000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateDetails()) {
      setActiveTab("details");
      return;
    }
    if (!validateMaterials()) {
      setActiveTab("material");
      return;
    }
    if (!validateQuantities()) {
      setActiveTab("quantities");
      return;
    }

    if (!giId) {
      toast.error("Goods inwards id is missing", { autoClose: 2500 });
      setActiveTab("details");
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("id");
      const statusToSave = giStatus === "active" ? "active" : "draft";
      const payload = {
        action: "update-goods-inwards",
        id: giId,
        status: statusToSave,
        ...buildHeaderFields(),
        materials: buildMaterialsPayload(),
      };
      if (form.giNumber) payload.giNumber = String(form.giNumber).trim();
      if (userId) payload.updatedBy = userId;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Goods inwards updated successfully",
          { autoClose: 2500 },
        );
        applyGiMetaFromResponse(responseData);
        const id = extractCreatedGiId(responseData) || giId;
        navigate(id ? `/goodsInwardsView/${id}` : "/goodsInwards");
      } else {
        const balanceData = responseData?.data;
        if (
          balanceData &&
          (balanceData.remaining != null || balanceData.acceptedSoFar != null)
        ) {
          toast.error(
            responseData?.message ||
              `Accepted quantity exceeds remaining PO balance (${balanceData.remaining ?? "-"})`,
            { autoClose: 4000 },
          );
        } else {
          toast.error(
            responseData?.message || "Failed to update goods inwards",
            { autoClose: 3000 },
          );
        }
      }
    } catch (error) {
      console.error("Update goods inwards error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while updating goods inwards",
        { autoClose: 3000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToTab = async (tabId) => {
    if (tabId === "details") {
      setActiveTab("details");
      return;
    }
    if (tabId === "material") {
      if (!validateDetails()) {
        setActiveTab("details");
        return;
      }
      const saved = await saveDraftFromDetails();
      if (!saved) return;
      setActiveTab("material");
      // Ensure PO materials for current PO (including unchanged GI Details)
      await ensurePoMaterialsForMaterialTab();
      return;
    }
    if (tabId === "quantities") {
      if (!giId) {
        const saved = await saveDraftFromDetails();
        if (!saved) return;
      }
      const updated = await updateDraftWithMaterials();
      if (!updated) return;
      setActiveTab("quantities");
      fetchStockForAllRows(materials);
    }
  };

  const handleNext = async () => {
    if (activeTab === "details") {
      if (!validateDetails()) return;
      const saved = await saveDraftFromDetails();
      if (!saved) return;
      setActiveTab("material");
      // Load PO materials for current PO (even if GI Details were unchanged)
      await ensurePoMaterialsForMaterialTab();
      return;
    }
    if (activeTab === "material") {
      const updated = await updateDraftWithMaterials();
      if (!updated) return;
      setActiveTab("quantities");
      fetchStockForAllRows(materials);
    }
  };

  const handlePrevious = () => {
    if (activeTab === "quantities") setActiveTab("material");
    else if (activeTab === "material") setActiveTab("details");
  };

  const tabClass = (tabId) =>
    `nav-link${activeTab === tabId ? " active" : ""}`;

  const cellClass = (rowKey, name) =>
    `form-control form-control-sm${
      fieldErrors[`mat_${rowKey}_${name}`] ? " border-danger" : ""
    }`;

  const renderMaterialsTable = ({ showQty = false, showStock = false } = {}) => (
    <div
      className="table-responsive"
      style={
        materialCodeDropdownRowKey
          ? { overflow: "visible" }
          : undefined
      }
    >
      <table className="table table-bordered table-sm mb-0">
        <thead className="thead-light">
          <tr>
            <th style={{ minWidth: 130 }}>Material Code *</th>
            <th style={{ minWidth: 220 }}>Description *</th>
            <th style={{ minWidth: 90 }}>HSN</th>
            <th style={{ minWidth: 70 }}>UOM</th>
            <th style={{ minWidth: 90 }}>PO Qty</th>
            <th style={{ minWidth: 90 }}>Balance</th>
            {showQty && (
              <>
                <th style={{ minWidth: 100 }}>Inward *</th>
                <th style={{ minWidth: 100 }}>Accepted *</th>
                <th style={{ minWidth: 100 }}>Rejected</th>
              </>
            )}
            {showStock && (
              <>
                <th style={{ minWidth: 90 }}>Stock</th>
                <th style={{ minWidth: 110 }}>Available</th>
                <th style={{ minWidth: 90 }}>Fetch</th>
              </>
            )}
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {materials.length === 0 ? (
            <tr>
              <td
                colSpan={showQty ? (showStock ? 13 : 10) : 7}
                className="text-center text-muted"
              >
                No materials added yet.{" "}
                {canAddStandaloneMaterials
                  ? "Click Add Material to create a line."
                  : "Select PO lines above and click Add, or select a supplier for a standalone GI."}
              </td>
            </tr>
          ) : (
            materials.map((row) => {
              const showMaterialCodeSearch = isStandaloneGi && !showStock;
              const filteredCatalog = showMaterialCodeSearch
                ? getFilteredMaterialCatalog(row.materialCode)
                : [];
              const showCodeDropdown =
                showMaterialCodeSearch &&
                materialCodeDropdownRowKey === row.rowKey &&
                String(row.materialCode || "").trim().length >= 1;

              return (
              <tr key={row.rowKey}>
                <td style={{ overflow: "visible", position: "relative" }}>
                  <div
                    style={{ position: "relative" }}
                    data-gi-material-code-search={
                      showMaterialCodeSearch ? "true" : undefined
                    }
                  >
                    <input
                      ref={(el) => {
                        if (el) materialCodeInputRefs.current[row.rowKey] = el;
                        else delete materialCodeInputRefs.current[row.rowKey];
                      }}
                      type="text"
                      className={cellClass(row.rowKey, "materialCode")}
                      value={row.materialCode}
                      placeholder={
                        showMaterialCodeSearch
                          ? "Search material code..."
                          : undefined
                      }
                      onFocus={() => {
                        if (showMaterialCodeSearch) {
                          openMaterialCodeDropdown(row.rowKey);
                        }
                      }}
                      onChange={(e) => {
                        updateMaterialRow(
                          row.rowKey,
                          "materialCode",
                          e.target.value,
                        );
                        if (showMaterialCodeSearch) {
                          openMaterialCodeDropdown(row.rowKey);
                        }
                      }}
                      autoComplete="off"
                    />
                    {showCodeDropdown &&
                      materialCodeDropdownRect &&
                      createPortal(
                        <div
                          className="dropdown-menu show"
                          data-gi-material-code-dropdown="true"
                          style={{
                            display: "block",
                            position: "fixed",
                            top: materialCodeDropdownRect.top,
                            left: materialCodeDropdownRect.left,
                            width: materialCodeDropdownRect.width,
                            maxHeight: 220,
                            overflowY: "auto",
                            zIndex: 3000,
                            margin: 0,
                          }}
                        >
                          {isLoadingMaterialCatalog ? (
                            <span className="dropdown-item text-muted">
                              Loading materials...
                            </span>
                          ) : filteredCatalog.length === 0 ? (
                            <span className="dropdown-item text-muted">
                              No materials found
                            </span>
                          ) : (
                            filteredCatalog.map((m) => {
                              const mapped = mapCatalogMaterialToGiFields(m);
                              const key =
                                mapped.materialMasterId ||
                                mapped.materialCode ||
                                mapped.materialDescription;
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className="dropdown-item"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() =>
                                    applyCatalogMaterialToRow(row.rowKey, m)
                                  }
                                >
                                  <strong>{mapped.materialCode || "-"}</strong>
                                  <br />
                                  <small className="text-muted">
                                    {mapped.materialDescription ||
                                      "No description"}
                                    {mapped.uom ? ` · ${mapped.uom}` : ""}
                                  </small>
                                </button>
                              );
                            })
                          )}
                        </div>,
                        document.body,
                      )}
                  </div>
                </td>
                <td>
                  <input
                    type="text"
                    className={cellClass(row.rowKey, "materialDescription")}
                    value={row.materialDescription}
                    onChange={(e) =>
                      updateMaterialRow(
                        row.rowKey,
                        "materialDescription",
                        e.target.value,
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.hsnCode}
                    onChange={(e) =>
                      updateMaterialRow(row.rowKey, "hsnCode", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.uom}
                    onChange={(e) =>
                      updateMaterialRow(row.rowKey, "uom", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.poQuantity}
                    readOnly
                    disabled
                    title="Original PO quantity"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.poBalanceQuantity}
                    readOnly
                    disabled
                    title="Remaining PO balance"
                  />
                </td>
                {showQty && (
                  <>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className={cellClass(row.rowKey, "inwardQuantity")}
                        value={row.inwardQuantity}
                        onChange={(e) =>
                          updateMaterialRow(
                            row.rowKey,
                            "inwardQuantity",
                            e.target.value,
                          )
                        }
                      />
                      {fieldErrors[`mat_${row.rowKey}_inwardQuantity`] && (
                        <small className="text-danger d-block mt-1">
                          {fieldErrors[`mat_${row.rowKey}_inwardQuantity`]}
                        </small>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className={cellClass(row.rowKey, "acceptedQuantity")}
                        value={row.acceptedQuantity}
                        onChange={(e) =>
                          updateMaterialRow(
                            row.rowKey,
                            "acceptedQuantity",
                            e.target.value,
                          )
                        }
                      />
                      {fieldErrors[`mat_${row.rowKey}_acceptedQuantity`] && (
                        <small className="text-danger d-block mt-1">
                          {fieldErrors[`mat_${row.rowKey}_acceptedQuantity`]}
                        </small>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className={cellClass(row.rowKey, "rejectedQuantity")}
                        value={row.rejectedQuantity}
                        readOnly
                        disabled
                        title="Rejected = Inward − Accepted"
                      />
                    </td>
                  </>
                )}
                {showStock && (
                  <>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={
                          stockLoadingKeys[row.rowKey]
                            ? "Loading..."
                            : row.stock !== "" && row.stock != null
                              ? row.stock
                              : "-"
                        }
                        readOnly
                        disabled
                        title="Current inventory stock (from inventory master)"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={computeAvailableStock(row)}
                        readOnly
                        disabled
                        title="Available = Stock + Accepted"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!!stockLoadingKeys[row.rowKey]}
                        onClick={() => fetchStockForRow(row)}
                        title="Refresh stock from inventory"
                      >
                        {stockLoadingKeys[row.rowKey] ? "..." : "Refresh"}
                      </button>
                    </td>
                  </>
                )}
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    title="Remove"
                    onClick={() => removeMaterialRow(row.rowKey)}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </td>
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  if (isLoadingGi) {
    return (
      <div className="content-header">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center py-5">
            <PulseLoader color="#1199B2" size={10} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Goods Inwards</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/goodsInwards">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline card-tabs">
                <div className="card-header p-0 pt-1 border-bottom-0">
                  <ul className="nav nav-tabs" role="tablist">
                    {TABS.map((tab) => (
                      <li className="nav-item" key={tab.id}>
                        <button
                          type="button"
                          className={tabClass(tab.id)}
                          onClick={() =>
                            tab.id === "details"
                              ? setActiveTab("details")
                              : goToTab(tab.id)
                          }
                        >
                          {tab.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body">
                  {activeTab === "details" && (
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>GI Number</label>
                          <input
                            type="text"
                            className="form-control"
                            value={form.giNumber || "-"}
                            readOnly
                            disabled
                          />
                          <small className="text-muted">
                            GI number is locked for this record
                          </small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>GI Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={form.giDate}
                            onChange={(e) => updateForm("giDate", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>
                            Invoice Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control${
                              fieldErrors.invoiceNumber ? " border-danger" : ""
                            }`}
                            value={form.invoiceNumber}
                            onChange={(e) =>
                              updateForm("invoiceNumber", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>Invoice Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={form.invoiceDate}
                            onChange={(e) =>
                              updateForm("invoiceDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>
                            Whose Stock <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-control${
                              fieldErrors.whoseStock ? " border-danger" : ""
                            }`}
                            value={form.whoseStock}
                            onChange={(e) =>
                              updateForm("whoseStock", e.target.value)
                            }
                          >
                            <option value="">Select whose stock</option>
                            {WHOSE_STOCK_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "material" && (
                    <>
                      <div className="row align-items-end mb-3">
                        <div className="col-md-4" ref={poSearchRef}>
                          <div className="form-group mb-0">
                            <label>Search by PO Number</label>
                            <div style={{ position: "relative" }}>
                              <input
                                type="text"
                                className={`form-control${
                                  fieldErrors.poNumber ? " border-danger" : ""
                                }`}
                                value={poSearchTerm || form.poNumber}
                                placeholder="Optional — search PO number..."
                                onFocus={() => {
                                  if (String(poSearchTerm || "").trim().length >= 1) {
                                    setShowPoDropdown(true);
                                  }
                                }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setPoSearchTerm(value);
                                  setShowPoDropdown(String(value).trim().length >= 1);
                                  if (!String(value).trim()) {
                                    setPoResults([]);
                                  }
                                  // Only clear GI materials when user leaves the selected PO
                                  if (
                                    form.poNumber &&
                                    String(value).trim() !==
                                      String(form.poNumber).trim()
                                  ) {
                                    updateForm("poNumber", "");
                                    updateForm("po_id", "");
                                    updateForm("poDate", "");
                                    clearMaterialSelection();
                                  }
                                }}
                                autoComplete="off"
                              />
                              {showPoDropdown &&
                                String(poSearchTerm || "").trim().length >= 1 && (
                                <div
                                  className="dropdown-menu show w-100"
                                  style={{
                                    display: "block",
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    marginTop: 0,
                                    paddingTop: 0,
                                    maxHeight: 240,
                                    overflowY: "auto",
                                    zIndex: 1050,
                                  }}
                                >
                                  {isSearchingPo ? (
                                    <span className="dropdown-item text-muted">
                                      Searching...
                                    </span>
                                  ) : poResults.length === 0 ? (
                                    <span className="dropdown-item text-muted">
                                      No POs found
                                    </span>
                                  ) : (
                                    poResults.map((po) => (
                                      <button
                                        key={po._id || po.id || po.poNumber}
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => applyPoSelection(po)}
                                      >
                                        <strong>
                                          {po.poNumber || po.poNo || "-"}
                                        </strong>
                                        <br />
                                        <small className="text-muted">
                                          {po.supplierName ||
                                            po.supplier?.name ||
                                            "No supplier"}
                                        </small>
                                      </button>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                            {isLoadingPoDetail && (
                              <small className="text-muted">Loading PO...</small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-0">
                            <label>PO Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={form.poDate}
                              onChange={(e) => updateForm("poDate", e.target.value)}
                              disabled={isStandaloneGi}
                            />
                          </div>
                        </div>
                        <div className="col-md-4" ref={supplierSearchRef}>
                          <div className="form-group mb-0">
                            <label>
                              Supplier Name <span className="text-danger">*</span>
                            </label>
                            <div style={{ position: "relative" }}>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className={`form-control${
                                    fieldErrors.supplierName
                                      ? " border-danger"
                                      : ""
                                  }`}
                                  value={
                                    supplierSearchTerm || form.supplierName
                                  }
                                  placeholder="Search supplier name..."
                                  onFocus={() => {
                                    if (
                                      String(
                                        supplierSearchTerm || "",
                                      ).trim().length >=
                                      SUPPLIER_SEARCH_MIN_LENGTH
                                    ) {
                                      setShowSupplierDropdown(true);
                                    }
                                  }}
                                  onChange={(e) =>
                                    handleSupplierSearchChange(e.target.value)
                                  }
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      const q = String(
                                        supplierSearchTerm || "",
                                      ).trim();
                                      if (
                                        q.length >= SUPPLIER_SEARCH_MIN_LENGTH
                                      ) {
                                        setShowSupplierDropdown(true);
                                        searchSuppliers(q, {
                                          showToast: true,
                                        });
                                      }
                                    }
                                  }}
                                  autoComplete="off"
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-blue"
                                    disabled={isSearchingSupplier}
                                    onClick={() => {
                                      const q = String(
                                        supplierSearchTerm ||
                                          form.supplierName ||
                                          "",
                                      ).trim();
                                      if (
                                        q.length >= SUPPLIER_SEARCH_MIN_LENGTH
                                      ) {
                                        setShowSupplierDropdown(true);
                                        searchSuppliers(q, {
                                          showToast: true,
                                        });
                                      }
                                    }}
                                  >
                                    {isSearchingSupplier ? (
                                      "..."
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              {showSupplierDropdown &&
                                String(supplierSearchTerm || "").trim()
                                  .length >= SUPPLIER_SEARCH_MIN_LENGTH && (
                                  <div
                                    className="dropdown-menu show w-100"
                                    style={{
                                      display: "block",
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      marginTop: 0,
                                      maxHeight: 240,
                                      overflowY: "auto",
                                      zIndex: 1050,
                                    }}
                                  >
                                    {isSearchingSupplier ? (
                                      <span className="dropdown-item text-muted">
                                        Searching...
                                      </span>
                                    ) : supplierResults.length === 0 ? (
                                      <span className="dropdown-item text-muted">
                                        No suppliers found
                                      </span>
                                    ) : (
                                      supplierResults.map((supplier) => {
                                        const sid =
                                          supplier.id ||
                                          supplier._id ||
                                          supplier.clientname;
                                        const label =
                                          getSupplierDisplayName(supplier) || "-";
                                        const contactCount =
                                          normalizeSupplierContacts(supplier)
                                            .length;
                                        return (
                                          <button
                                            key={sid}
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() =>
                                              applySupplierSelection(supplier)
                                            }
                                          >
                                            <strong>{label}</strong>
                                            {(supplier.city ||
                                              supplier.contactPerson ||
                                              contactCount > 0) && (
                                              <>
                                                <br />
                                                <small className="text-muted">
                                                  {[
                                                    supplier.contactPerson,
                                                    supplier.city,
                                                    contactCount > 0
                                                      ? `${contactCount} email${
                                                          contactCount > 1
                                                            ? "s"
                                                            : ""
                                                        }`
                                                      : "",
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                                </small>
                                              </>
                                            )}
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {hasSupplierSelected && supplierContactOptions.length > 0 && (
                        <div className="row mb-3">
                          <div className="col-12">
                            <div className="border rounded p-3 bg-light">
                              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                                <label className="mb-0 fwt_600">
                                  Supplier Emails
                                </label>
                                <small className="text-muted">
                                  Select one or more contacts to notify
                                </small>
                              </div>
                              <div className="row">
                                {supplierContactOptions.map((contact) => {
                                  const checked =
                                    selectedSupplierContactKeys.has(
                                      contact.key,
                                    );
                                  const inputId = `gi-edit-supplier-contact-${contact.key}`;
                                  return (
                                    <div
                                      className="col-md-6 col-lg-4 mb-2"
                                      key={contact.key}
                                    >
                                      <div className="custom-control custom-checkbox">
                                        <input
                                          type="checkbox"
                                          className="custom-control-input"
                                          id={inputId}
                                          checked={checked}
                                          onChange={() =>
                                            toggleSupplierContact(contact.key)
                                          }
                                        />
                                        <label
                                          className="custom-control-label"
                                          htmlFor={inputId}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <span className="d-block">
                                            {contact.supplierMailId}
                                          </span>
                                          {(contact.contactPerson ||
                                            contact.supplierPhoneNumber) && (
                                            <small className="text-muted d-block">
                                              {[
                                                contact.contactPerson,
                                                contact.supplierPhoneNumber,
                                              ]
                                                .filter(Boolean)
                                                .join(" · ")}
                                            </small>
                                          )}
                                        </label>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {selectedSupplierContactKeys.size === 0 && (
                                <small className="text-danger d-block mt-1">
                                  Select at least one supplier email if you want
                                  contacts on this GI.
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {!form.po_id ? (
                        canAddStandaloneMaterials ? (
                          <div className="alert alert-light border mb-3 d-flex justify-content-between align-items-center flex-wrap">
                            <span className="mb-0">
                              Standalone GI (no PO) — add material lines
                              manually for{" "}
                              <strong>{form.supplierName}</strong>.
                            </span>
                          </div>
                        ) : (
                          <div className="alert alert-light border mb-3">
                            Select a PO to load material lines, or search and
                            select a supplier to create a standalone GI.
                          </div>
                        )
                      ) : poMaterials.length === 0 ? (
                        <div className="alert alert-light border mb-3">
                          {isLoadingPoDetail
                            ? "Loading PO materials..."
                            : "No material lines on this PO"}
                        </div>
                      ) : (
                        <div
                          className="mb-3"
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "6px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            className="d-flex justify-content-between align-items-center flex-wrap"
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#f8f9fa",
                              gap: "8px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setPoMaterialResultsOpen((open) => !open)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setPoMaterialResultsOpen((open) => !open);
                              }
                            }}
                          >
                            <span
                              className="d-flex align-items-center"
                              style={{
                                fontWeight: 600,
                                fontSize: "13px",
                                gap: "8px",
                              }}
                            >
                              <i
                                className={`fa-solid ${
                                  poMaterialResultsOpen
                                    ? "fa-chevron-up"
                                    : "fa-chevron-down"
                                }`}
                                style={{ width: "14px", color: "#003366" }}
                                aria-hidden
                              />
                              PO material results — {countPendingPoSelection()}{" "}
                              to add, {countAddedInCurrentResults()} already
                              added
                            </span>
                            {poMaterialResultsOpen && (
                              <button
                                type="button"
                                className="btn btn-sm btn-blue"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addSelectedPoMaterials();
                                }}
                                disabled={countPendingPoSelection() === 0}
                              >
                                Add
                              </button>
                            )}
                          </div>

                          {poMaterialResultsOpen && (
                            <div style={{ maxHeight: "360px", overflowY: "auto" }}>
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor: "#003366",
                                      color: "#fff",
                                    }}
                                  >
                                    <th style={{ fontSize: "11px", width: "36px" }}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          getSelectablePoKeys().length > 0 &&
                                          getSelectablePoKeys().every((key) =>
                                            selectedPoKeys.includes(key),
                                          )
                                        }
                                        onChange={toggleSelectAllPoMaterials}
                                        title="Select all not yet added"
                                      />
                                    </th>
                                    <th style={{ fontSize: "11px" }}>
                                      Material Code
                                    </th>
                                    <th style={{ fontSize: "11px" }}>
                                      Material Description
                                    </th>
                                    <th style={{ fontSize: "11px" }}>HSN</th>
                                    <th style={{ fontSize: "11px" }}>UOM</th>
                                    <th style={{ fontSize: "11px" }}>PO Qty</th>
                                    <th style={{ fontSize: "11px" }}>Balance</th>
                                    <th style={{ fontSize: "11px" }}>
                                      Accepted so far
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredPoMaterials.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan="8"
                                        className="text-center text-muted"
                                      >
                                        No materials match your search
                                      </td>
                                    </tr>
                                  ) : (
                                    filteredPoMaterials.map((m) => {
                                      const isAdded = isPoMaterialAdded(m);
                                      const isClosed = isPoMaterialClosed(m);
                                      const isSelected = selectedPoKeys.includes(
                                        m.sourceKey,
                                      );
                                      const currentGiId = String(
                                        giId || id || "",
                                      ).trim();
                                      // Show other draft GIs only — so user knows which GI already holds qty
                                      const reservations = (
                                        Array.isArray(m.reservations)
                                          ? m.reservations
                                          : []
                                      ).filter(
                                        (r) =>
                                          r.status === "draft" &&
                                          String(r.giId || "").trim() !==
                                            currentGiId,
                                      );
                                      const hasReservations =
                                        reservations.length > 0;
                                      return (
                                        <React.Fragment key={m.sourceKey}>
                                          <tr
                                            style={{
                                              backgroundColor: isAdded
                                                ? "#e8f5e9"
                                                : isClosed
                                                  ? "#f5f5f5"
                                                  : isSelected
                                                    ? "#e8f4f8"
                                                    : undefined,
                                              opacity: isClosed ? 0.75 : 1,
                                            }}
                                          >
                                            <td className="text-center">
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={isAdded || isClosed}
                                                onChange={() =>
                                                  togglePoMaterialKey(m.sourceKey)
                                                }
                                                title={
                                                  isAdded
                                                    ? "Already added to GI materials"
                                                    : isClosed
                                                      ? hasReservations
                                                        ? "No balance — qty held by another draft GI"
                                                        : "No remaining balance"
                                                      : ""
                                                }
                                              />
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.materialCode)}
                                              {hasReservations && (
                                                <span
                                                  className="badge badge-warning ml-1"
                                                  style={{ fontSize: "9px" }}
                                                  title="Already reserved on another draft GI — open that GI to update"
                                                >
                                                  Draft GI
                                                </span>
                                              )}
                                            </td>
                                            <td
                                              style={{
                                                fontSize: "12px",
                                                maxWidth: "180px",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {toDisplay(m.materialDescription)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.hsnCode)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.uom)}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.poQuantity)}
                                              {isAdded && (
                                                <span
                                                  className="badge badge-success ml-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Added
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              <strong>
                                                {toDisplay(m.poBalanceQuantity)}
                                              </strong>
                                              {isClosed && (
                                                <span
                                                  className="badge badge-secondary ml-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Closed
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                              {toDisplay(m.acceptedSoFar)}
                                              {(m.acceptedActive !== "" ||
                                                m.acceptedDraft !== "") && (
                                                <div
                                                  className="text-muted"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Active:{" "}
                                                  {toDisplay(m.acceptedActive)}
                                                  {" · "}
                                                  Draft:{" "}
                                                  {toDisplay(m.acceptedDraft)}
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                          {hasReservations && (
                                            <tr>
                                              <td
                                                colSpan="8"
                                                style={{
                                                  backgroundColor: "#fff8e6",
                                                  padding: "8px 12px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    marginBottom: "4px",
                                                    color: "#6c4a00",
                                                  }}
                                                >
                                                  Draft GI reservations (
                                                  {reservations.length})
                                                </div>
                                                <div
                                                  className="text-muted mb-2"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  This material qty is already used
                                                  on another draft GI. Open that GI
                                                  to update it — no remaining
                                                  balance for a new line here.
                                                </div>
                                                <table
                                                  className="table table-sm table-bordered mb-0"
                                                  style={{ background: "#fff" }}
                                                >
                                                  <thead>
                                                    <tr
                                                      style={{
                                                        backgroundColor:
                                                          "#f0e6d2",
                                                      }}
                                                    >
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        GI Number
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        GI Date
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Status
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Accepted
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Inward
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Rejected
                                                      </th>
                                                      <th
                                                        style={{
                                                          fontSize: "10px",
                                                        }}
                                                      >
                                                        Created By
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {reservations.map((r) => (
                                                      <tr
                                                        key={r.key}
                                                        style={{
                                                          backgroundColor:
                                                            "#fff3cd",
                                                        }}
                                                      >
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {r.giId ? (
                                                            <Link
                                                              to={`/goodsInwardsEdit/${r.giId}`}
                                                              style={{
                                                                color: "#003366",
                                                                fontWeight: 600,
                                                              }}
                                                              title="Open this draft GI to update quantities"
                                                            >
                                                              {toDisplay(
                                                                r.giNumber,
                                                              )}
                                                            </Link>
                                                          ) : (
                                                            toDisplay(r.giNumber)
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {formatReservationDate(
                                                            r.giDate,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          <span
                                                            className="badge badge-warning"
                                                            style={{
                                                              fontSize: "9px",
                                                              textTransform:
                                                                "capitalize",
                                                            }}
                                                          >
                                                            {toDisplay(r.status)}
                                                          </span>
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {toDisplay(
                                                            r.acceptedQuantity,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {toDisplay(
                                                            r.inwardQuantity,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {toDisplay(
                                                            r.rejectedQuantity,
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            fontSize: "11px",
                                                          }}
                                                        >
                                                          {toDisplay(
                                                            r.createdByName,
                                                          )}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">
                          GI Materials ({materials.length})
                        </h5>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          {fieldErrors.materials && (
                            <small className="text-danger">
                              {fieldErrors.materials}
                            </small>
                          )}
                          {canAddStandaloneMaterials && (
                            <button
                              type="button"
                              className="btn btn-sm btn-blue"
                              onClick={addStandaloneMaterialRow}
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                      {renderMaterialsTable({ showQty: true, showStock: false })}
                      <small className="text-muted d-block mt-2">
                        {isStandaloneGi
                          ? "Rejected is auto-calculated as Inward − Accepted."
                          : "Rejected is auto-calculated as Inward − Accepted. Accepted cannot exceed PO Qty (or remaining Balance)."}
                      </small>
                    </>
                  )}

                  {activeTab === "quantities" && (
                    <>
                      <h5 className="mb-2">Quantities & Stock</h5>
                      {renderMaterialsTable({ showQty: true, showStock: true })}
                      <small className="text-muted d-block mt-2">
                        Stock is loaded from inventory (read-only). Available =
                        Stock + Accepted. Rejected = Inward − Accepted.
                      </small>
                    </>
                  )}
                </div>

                <div className="card-footer d-flex justify-content-between">
                  <div>
                    {activeTab !== "details" && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handlePrevious}
                        disabled={isSubmitting}
                      >
                        Previous
                      </button>
                    )}
                  </div>
                  <div>
                    {activeTab !== "quantities" ? (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleNext}
                        disabled={
                          isSubmitting ||
                          (activeTab === "material" &&
                            (hasInvalidMaterialQuantities || !materials.length))
                        }
                        title={
                          activeTab === "material" && hasInvalidMaterialQuantities
                            ? "Fix quantity errors before continuing"
                            : undefined
                        }
                      >
                        {isSubmitting ? "Saving..." : "Next"}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={handleUpdate}
                          disabled={isSubmitting || hasInvalidMaterialQuantities}
                        >
                          {isSubmitting ? "Updating..." : "Update"}
                        </button>
                        {giStatus !== "active" && (
                          <button
                            type="button"
                            className="btn btn-blue ml-2"
                            onClick={handleActivate}
                            disabled={
                              isSubmitting || hasInvalidMaterialQuantities
                            }
                          >
                            {isSubmitting ? "Activating..." : "Activate"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoodsInwardsEdit;
